import { AionCta } from "@/components/site/AionCta";
import { OG_ABOUT } from "@/lib/og-images";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Megaphone } from "lucide-react";
import { PageBreadcrumb } from "@/components/site/PageBreadcrumb";
import { RelatedKeywords } from "@/components/site/RelatedKeywords";
import { RelatedPages } from "@/components/site/RelatedPages";

const TITLE = "Raj Shamani Speaker — Keynotes, Topics & Booking Enquiries";
const DESC =
  "Raj Shamani has spoken on 150+ stages across 25+ countries, including the United Nations. His keynote topics, audiences and how to book him for an event.";
const URL = "https://rajshamani.life/raj-shamani-speaker";
const OG = OG_ABOUT;

const topics = [
  {
    title: "Building without permission",
    who: "Students & first-time founders",
    detail:
      "The core argument of Build, Don't Talk — why output beats credentials, and how to start a business in India without capital, contacts or a degree.",
  },
  {
    title: "The creator economy as a business model",
    who: "Marketers & media teams",
    detail:
      "How attention converts into distribution, and why brands increasingly buy trust from creators instead of impressions from platforms.",
  },
  {
    title: "Lessons from 200+ founder interviews",
    who: "Corporate leadership & conferences",
    detail:
      "Patterns from the Figuring Out podcast — what separates founders who compound from founders who stall.",
  },
  {
    title: "D2C and the Indian consumer",
    who: "Retail, FMCG & investors",
    detail:
      "What House of X has learned building and backing direct-to-consumer brands for an Indian buyer, not a copy-pasted Western one.",
  },
  {
    title: "AI leverage for founders",
    who: "Founders, operators & students",
    detail:
      "Where AI genuinely removes work in an Indian business, what it cannot do yet, and how small teams use it to compete above their size.",
  },
];

const faq = [
  {
    q: "Is Raj Shamani a motivational speaker?",
    a: "He is usually described as a business speaker rather than a motivational one. His talks focus on execution, distribution and business models, drawn from running Shamani Industries, House of X and the Figuring Out podcast.",
  },
  {
    q: "Where has Raj Shamani spoken?",
    a: "He has spoken on more than 150 stages across 25+ countries, including at the United Nations, as well as at Indian universities, industry conferences and corporate events.",
  },
  {
    q: "How do I book Raj Shamani for an event?",
    a: "Speaking enquiries go through the official contact page or partnerships@figuringout.co. Include the event date, city, audience size, format and budget — enquiries without those details are hard to evaluate.",
  },
  {
    q: "What is Raj Shamani's speaking fee?",
    a: "Speaking fees are not published. They vary by event format, location, duration and whether the engagement includes content or a podcast recording, so they are quoted per enquiry.",
  },
  {
    q: "Does Raj Shamani speak at college events?",
    a: "Yes, he regularly speaks at universities and student festivals in India. College enquiries follow the same process as corporate ones and should be sent well in advance of the event date.",
  },
];

export const Route = createFileRoute("/raj-shamani-speaker")({
  head: () => ({
    meta: [
      { title: TITLE, key: "title" },
      { name: "description", content: DESC, key: "description" },
      { property: "og:title", content: TITLE, key: "og:title" },
      { property: "og:description", content: DESC, key: "og:description" },
      { property: "og:url", content: URL, key: "og:url" },
      { property: "og:type", content: "article", key: "og:type" },
      { property: "og:image", content: OG, key: "og:image" },
      { property: "og:image:alt", content: "Raj Shamani speaking on stage", key: "og:image:alt" },
      { name: "twitter:card", content: "summary_large_image", key: "twitter:card" },
      { name: "twitter:title", content: TITLE, key: "twitter:title" },
      { name: "twitter:description", content: DESC, key: "twitter:description" },
      { name: "twitter:image", content: OG, key: "twitter:image" },
      { property: "og:locale", content: "en_IN", key: "og:locale" },
      { name: "geo.region", content: "IN", key: "geo.region" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          "@id": `${URL}#webpage`,
          name: TITLE,
          headline: TITLE,
          description: DESC,
          url: URL,
          inLanguage: "en",
          isPartOf: { "@type": "WebSite", "@id": "https://rajshamani.life/#website" },
          publisher: { "@type": "Organization", "@id": "https://rajshamani.life/#organization" },
          primaryImageOfPage: { "@type": "ImageObject", url: OG },
          image: OG,
          about: {
            "@type": "Person",
            "@id": "https://rajshamani.life/about#person",
            name: "Raj Shamani",
            jobTitle: "Entrepreneur, investor and speaker",
            url: "https://rajshamani.life/about",
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "@id": `${URL}#faq`,
          mainEntity: faq.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: SpeakerPage,
});

function SpeakerPage() {
  return (
    <div>
      <PageBreadcrumb current="Raj Shamani Speaker" />

      <section className="bg-noir border-b border-border/50">
        <div className="mx-auto max-w-5xl px-6 py-24">
          <div className="text-xs tracking-widest uppercase text-gold mb-4 inline-flex items-center gap-2">
            <Megaphone size={14} /> Speaking
          </div>
          <h1 className="text-4xl md:text-6xl font-bold leading-tight">
            Raj Shamani as a <span className="text-gradient-gold">speaker</span>
          </h1>
          <p className="mt-8 text-lg text-muted-foreground max-w-3xl leading-relaxed">
            Raj Shamani has spoken on more than{" "}
            <strong className="text-foreground">150 stages across 25+ countries</strong>, including
            the United Nations. His talks are business talks — building, distribution, the creator
            economy and AI leverage — delivered to founders, students and corporate teams.
          </p>
          <Link
            to="/contact"
            className="mt-8 inline-flex items-center rounded-full bg-gold px-6 py-3 text-sm font-semibold text-noir"
          >
            Send a speaking enquiry
          </Link>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-6 py-20">
        <h2 className="text-3xl md:text-4xl font-bold mb-8">Keynote topics</h2>
        <div className="grid sm:grid-cols-2 gap-6">
          {topics.map((t) => (
            <div key={t.title} className="p-6 rounded-xl border border-border bg-card">
              <div className="text-xs uppercase tracking-widest text-gold mb-2">{t.who}</div>
              <h3 className="font-bold mb-2">{t.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{t.detail}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-6 pb-20">
        <h2 className="text-3xl md:text-4xl font-bold mb-6">What to include in an enquiry</h2>
        <ul className="space-y-3 text-muted-foreground max-w-3xl leading-relaxed list-disc pl-5">
          <li>Event name, date and city (or confirm it is virtual).</li>
          <li>Audience — who they are, roughly how many, and what they came for.</li>
          <li>Format: keynote, fireside chat, panel or workshop, and the time slot.</li>
          <li>Budget range, and whether travel and stay are covered.</li>
          <li>Any content or recording rights you need afterwards.</li>
        </ul>
        <p className="mt-6 text-muted-foreground max-w-3xl">
          Send it through the{" "}
          <Link to="/contact" className="text-gold underline underline-offset-4">
            contact page
          </Link>
          . For podcast appearances instead, use the{" "}
          <Link to="/guest" className="text-gold underline underline-offset-4">
            guest pitch page
          </Link>
          .
        </p>
      </section>

      <section className="mx-auto max-w-5xl px-6 pb-20">
        <h2 className="text-3xl md:text-4xl font-bold mb-8">Frequently asked questions</h2>
        <div className="space-y-6">
          {faq.map((f) => (
            <div key={f.q} className="rounded-xl border border-border bg-card p-6">
              <h3 className="font-bold mb-2">{f.q}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <AionCta />
      <RelatedKeywords currentPath="/raj-shamani-speaker" />
      <RelatedPages pages={["contact", "guest", "about", "book", "ventures", "aion"]} />
    </div>
  );
}
